import React, { useState, useRef, useEffect } from 'react';
import { Form, Row, Col, Table, Button, Badge, ProgressBar, Modal, Alert, OverlayTrigger, Tooltip, Spinner } from 'react-bootstrap';
import Select from 'react-select';
import { analyzeDocumentContent } from '../../api/poAPI';

const InMemoryDocuments = ({
    pendingDocuments,
    setPendingDocuments,
    onExtractedData,
    poId,
    existingDocuments = [],  // Existing documents for edit mode
    isEditMode = false,       // Whether we're in edit mode
    onDocumentsToDelete,      // Callback to pass documentsToDelete to parent
    documentUploadProgress = {}, // Upload progress from parent
    isDocumentUploading = false,  // Overall upload state from parent
    disabled = false,         // Disable all controls when true
    onDownloadDocument       // Callback to handle document downloads
}) => {
    const fileInputRef = useRef(null);
    const [documentForm, setDocumentForm] = useState({
        type: null,
        file: null,
        description: ''
    });
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [documentToDelete, setDocumentToDelete] = useState(null);
    const [analyzingDocuments, setAnalyzingDocuments] = useState(new Set());
    const [documentsToDelete, setDocumentsToDelete] = useState([]); // Track existing docs marked for deletion
    const [localUploadProgress, setLocalUploadProgress] = useState({}); // Local upload progress for individual docs

    // Notify parent component when documentsToDelete changes
    useEffect(() => {
        if (onDocumentsToDelete) {
            onDocumentsToDelete(documentsToDelete);
        }
    }, [documentsToDelete, onDocumentsToDelete]);

    // Simulate upload progress when documents are being uploaded
    useEffect(() => {
        if (isDocumentUploading && pendingDocuments.length > 0) {
            const simulateProgress = () => {
                pendingDocuments.forEach((doc, index) => {
                    if (!localUploadProgress[doc.id]) {
                        // Start progress simulation for each document
                        let progress = 0;
                        const interval = setInterval(() => {
                            progress += Math.random() * 15; // Random progress increment
                            if (progress >= 100) {
                                progress = 100;
                                clearInterval(interval);
                            }
                            setLocalUploadProgress(prev => ({
                                ...prev,
                                [doc.id]: Math.min(progress, 100)
                            }));
                        }, 200 + (index * 100)); // Stagger start times
                        
                        // Clean up after 10 seconds
                        setTimeout(() => {
                            clearInterval(interval);
                        }, 10000);
                    }
                });
            };
            
            simulateProgress();
        } else if (!isDocumentUploading) {
            // Clear progress when upload is complete
            setLocalUploadProgress({});
        }
    }, [isDocumentUploading, pendingDocuments]);

    const documentTypes = [
        { value: 'BOL', label: 'Bill of Lading' },
        { value: 'INV', label: 'Commercial Invoice' },
        { value: 'PI', label: 'Proforma Invoice' },
        { value: 'PCK', label: 'Packing Slip' },
        { value: 'DO', label: 'Delivery from Port' },
        { value: 'FI', label: 'Freight Invoice' },
        { value: 'ISF', label: 'Importer Security Filing' },
        { value: 'OTH', label: 'Other Documents' }
    ];

    const handleFileSelect = (event) => {
        const file = event.target.files[0];
        if (file) {
            setDocumentForm(prev => ({
                ...prev,
                file: file
            }));
        }
    };

    const handleAddDocument = () => {
        if (!documentForm.type || !documentForm.file) return;

        const tempDoc = {
            id: `temp-${Date.now()}`,
            file: documentForm.file,
            document_type: documentForm.type.value,
            notes: documentForm.description,
            extractedData: null,
            isAnalyzing: false,
            fileName: documentForm.file.name
        };

        // Add document to pending list without analysis
        setPendingDocuments(prev => [...prev, tempDoc]);

        // Reset form
        setDocumentForm({
            type: null,
            file: null,
            description: ''
        });
        fileInputRef.current.value = '';
    };

    const handleAnalyzeDocument = async () => {
        if (!documentForm.type || !documentForm.file) return;

        const tempDoc = {
            id: `temp-${Date.now()}`,
            file: documentForm.file,
            document_type: documentForm.type.value,
            notes: documentForm.description,
            extractedData: null,
            isAnalyzing: true,
            fileName: documentForm.file.name
        };

        // Add document to pending list
        setPendingDocuments(prev => [...prev, tempDoc]);
        setAnalyzingDocuments(prev => new Set([...prev, tempDoc.id]));

        // Analyze document content
        try {
            const formData = new FormData();
            formData.append('file', documentForm.file);
            formData.append('document_type', documentForm.type.value);
            if (poId) {
                formData.append('po_id', poId);  // Include PO ID for context
            }

            const analysisResult = await analyzeDocumentContent(formData);

            // Update document with analysis results
            setPendingDocuments(prev => 
                prev.map(doc => doc.id === tempDoc.id 
                    ? {
                        ...doc, 
                        extractedData: analysisResult.extracted_data,
                        isAnalyzing: false
                    }
                    : doc
                )
            );

            // Pass extracted data to parent for field population
            if (analysisResult.extracted_data && onExtractedData) {
                onExtractedData(analysisResult.extracted_data);
            }

        } catch (error) {
            console.error('Document analysis failed:', error);
            // Update document to show analysis failed
            setPendingDocuments(prev => 
                prev.map(doc => doc.id === tempDoc.id 
                    ? {...doc, isAnalyzing: false, analysisError: error.message}
                    : doc
                )
            );
        } finally {
            setAnalyzingDocuments(prev => {
                const newSet = new Set(prev);
                newSet.delete(tempDoc.id);
                return newSet;
            });
        }

        // Reset form
        setDocumentForm({
            type: null,
            file: null,
            description: ''
        });
        fileInputRef.current.value = '';
    };

    const handleRemoveDocument = (docId, isExisting = false) => {
        if (isExisting) {
            // Mark existing document for deletion
            const docToDelete = existingDocuments.find(doc => doc._id === docId);
            if (docToDelete) {
                setDocumentsToDelete(prev => [...prev, docToDelete]);
            }
        } else {
            // Remove pending document from memory
            setPendingDocuments(prev => prev.filter(doc => doc.id !== docId));
        }
        setShowDeleteModal(false);
        setDocumentToDelete(null);
    };

    const confirmDelete = (doc, isExisting = false) => {
        setDocumentToDelete({...doc, isExisting});
        setShowDeleteModal(true);
    };

    // Get display documents (existing + pending - deleted)
    const getDisplayDocuments = () => {
        const existingDocsArray = Array.isArray(existingDocuments) ? existingDocuments : [];
        const existingDocs = existingDocsArray
            .filter(doc => !documentsToDelete.find(d => d._id === doc._id))
            .map(doc => ({
                ...doc,
                id: `existing-${doc._id}`,
                isExisting: true,
                fileName: doc.file_name || 'Unknown',
                document_type: doc.document_type,
                notes: doc.notes
            }));

        const pendingDocs = pendingDocuments.map(doc => ({
            ...doc,
            isExisting: false
        }));

        return [...existingDocs, ...pendingDocs];
    };

    const getDocumentTypeLabel = (type) => {
        const docType = documentTypes.find(dt => dt.value === type);
        return docType ? docType.label : type;
    };

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    return (
        <div className="documents-section">
            <h6>Documents</h6>
            
            {/* Document Upload Form - Only show when not disabled */}
            {!disabled && (
                <>
            <Row className="mb-3">
                <Col md={4}>
                    <Form.Group>
                        <Form.Label>Document Type</Form.Label>
                        <Select
                            value={documentForm.type}
                            onChange={(selectedOption) => setDocumentForm(prev => ({...prev, type: selectedOption}))}
                            options={documentTypes}
                            placeholder="Select document type..."
                            isSearchable={false}
                            isDisabled={disabled}
                            className="react-select-container"
                            classNamePrefix="react-select"
                        />
                    </Form.Group>
                </Col>
                <Col md={4}>
                    <Form.Group>
                        <Form.Label>File</Form.Label>
                        <Form.Control
                            type="file"
                            ref={fileInputRef}
                            onChange={handleFileSelect}
                            accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.txt"
                            disabled={disabled}
                            autoComplete="off"
                            spellCheck="false"
                        />
                    </Form.Group>
                </Col>
                <Col md={4}>
                    <Form.Group>
                        <Form.Label>Description</Form.Label>
                        <Form.Control
                            type="text"
                            value={documentForm.description}
                            onChange={(e) => setDocumentForm(prev => ({...prev, description: e.target.value}))}
                            placeholder="Optional description..."
                            disabled={disabled}
                            autoComplete="off"
                            spellCheck="false"
                        />
                    </Form.Group>
                </Col>
            </Row>
            
            <Row className="mb-3">
                <Col>
                    <div className="d-flex gap-2">
                        <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip>Add document and automatically extract data to populate form fields</Tooltip>}
                        >
                            <Button 
                                variant="success" 
                                onClick={handleAnalyzeDocument}
                                disabled={!documentForm.type || !documentForm.file || analyzingDocuments.size > 0 || disabled}
                                size="sm"
                            >
                                {analyzingDocuments.size > 0 ? (
                                    <>
                                        <Spinner
                                            as="span"
                                            animation="border"
                                            size="sm"
                                            role="status"
                                            aria-hidden="true"
                                            className="me-2"
                                        />
                                        Analyzing...
                                    </>
                                ) : (
                                    <>
                                        <i className="fas fa-plus me-1"></i>
                                        Add and Analyze
                                    </>
                                )}
                            </Button>
                        </OverlayTrigger>
                        <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip>Add document to upload list without data extraction</Tooltip>}
                        >
                            <Button 
                                variant="outline-primary" 
                                onClick={handleAddDocument}
                                disabled={!documentForm.type || !documentForm.file || disabled}
                                size="sm"
                            >
                                <i className="fas fa-plus me-1"></i>
                                Add
                            </Button>
                        </OverlayTrigger>
                    </div>
                </Col>
            </Row>
                </>
            )}

            {/* Documents List (Existing + Pending) */}
            {(() => {
                const displayDocuments = getDisplayDocuments();
                return displayDocuments.length > 0 ? (
                    <div className="mt-3">
                        <Table striped bordered hover size="sm">
                            <thead>
                                <tr>
                                    <th>Type</th>
                                    <th>File Name</th>
                                    <th>Size</th>
                                    <th>Status</th>
                                    <th>Description</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {displayDocuments.map((doc) => (
                                    <tr key={doc.id}>
                                        <td>
                                            <Badge bg="secondary">
                                                {getDocumentTypeLabel(doc.document_type)}
                                            </Badge>
                                        </td>
                                        <td>{doc.fileName}</td>
                                        <td>
                                            {doc.isExisting 
                                                ? 'Existing' 
                                                : formatFileSize(doc.file?.size || 0)
                                            }
                                        </td>
                                        <td>
                                            {doc.isExisting ? (
                                                <Badge bg="info">Saved</Badge>
                                            ) : doc.isAnalyzing ? (
                                                <div className="d-flex align-items-center gap-2">
                                                    <Spinner size="sm" animation="border" />
                                                    <small>Analyzing...</small>
                                                </div>
                                            ) : doc.analysisError ? (
                                                <Badge bg="danger">Analysis Failed</Badge>
                                            ) : (documentUploadProgress[doc.id] || localUploadProgress[doc.id]) ? (
                                                <div style={{ minWidth: '100px' }}>
                                                    <ProgressBar 
                                                        now={documentUploadProgress[doc.id] || localUploadProgress[doc.id]} 
                                                        size="sm" 
                                                        label={`${Math.round(documentUploadProgress[doc.id] || localUploadProgress[doc.id])}%`}
                                                        style={{ height: '20px' }}
                                                        variant={(documentUploadProgress[doc.id] || localUploadProgress[doc.id]) >= 100 ? 'success' : 'primary'}
                                                    />
                                                </div>
                                            ) : isDocumentUploading ? (
                                                <div className="d-flex align-items-center gap-2">
                                                    <Spinner size="sm" animation="border" />
                                                    <small>Uploading...</small>
                                                </div>
                                            ) : (
                                                <Badge bg="warning">Pending</Badge>
                                            )}
                                        </td>
                                        <td>{doc.notes || '-'}</td>
                                        <td>
                                            <div className="d-flex gap-1">
                                                {/* Download button - only for existing/saved documents */}
                                                {doc.isExisting && onDownloadDocument && (
                                                    <OverlayTrigger
                                                        placement="top"
                                                        overlay={<Tooltip>Download document</Tooltip>}
                                                    >
                                                        <Button
                                                            variant="outline-primary"
                                                            size="sm"
                                                            onClick={() => onDownloadDocument(doc._id)}
                                                        >
                                                            <i className="bi bi-download"></i>
                                                        </Button>
                                                    </OverlayTrigger>
                                                )}
                                                
                                                {/* Delete/Remove button */}
                                                <Button
                                                    variant="outline-danger"
                                                    size="sm"
                                                    onClick={() => confirmDelete(doc, doc.isExisting)}
                                                    disabled={disabled}
                                                >
                                                    {doc.isExisting ? 'Delete' : 'Remove'}
                                                </Button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                ) : (
                    <Alert variant="info" className="mt-3">
                        {disabled 
                            ? 'No documents available for this confirmation.'
                            : (isEditMode 
                                ? 'No documents. Add documents to be uploaded when the confirmation is updated.'
                                : 'No documents selected. Documents will be uploaded when the confirmation is saved.')
                        }
                    </Alert>
                );
            })()}

            {/* Delete Confirmation Modal */}
            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Confirm Delete</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Are you sure you want to remove this document? This action cannot be undone.
                    {documentToDelete && (
                        <div className="mt-2">
                            <strong>Document:</strong> {documentToDelete.fileName}
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
                        Cancel
                    </Button>
                    <Button 
                        variant="danger" 
                        onClick={() => handleRemoveDocument(documentToDelete?.isExisting ? documentToDelete._id : documentToDelete?.id, documentToDelete?.isExisting)}
                        disabled={disabled}
                    >
                        Remove Document
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default InMemoryDocuments;